/**
 * Event config for the NZ chapter.
 *
 * The NZ site has no standing events index (unlike the AU twin) — it
 * carries one-off community sessions, each reachable at
 * /events/<slug> so a flyer's QR code or a share link can deep-link
 * straight into registration. Append a row to add another.
 *
 * Registration is wired to the shared backend's `registerForEvent`
 * mutation; the worker reads the request Origin and routes NZ traffic
 * through the NZ Postmark account + inbox, so tickets send under the
 * Vihangam Yog New Zealand brand automatically.
 */

export type EventKind = "community";
export type EventFormat = "in-person" | "online" | "hybrid";

export interface CommunityVenue {
  name: string;
  hall?: string;
  suburb: string;
  /** A google.com/maps URL — opens in the visitor's default maps app. */
  mapUrl: string;
}

export interface VyEvent {
  slug: string;
  kind: EventKind;
  title: string;
  /** A short marketing line for cards. One sentence, no theatrics. */
  blurb: string;
  /** Longer copy shown on the registration page. Plain text. */
  longCopy?: string;
  /** ISO start for sortability + lifecycle phase. */
  startsAt?: string;
  /** ISO end. Drives the "past event" cutoff. Falls back to
   *  startsAt + 4h when omitted. */
  endsAt?: string;
  /** Display strings — formatted once so the UI doesn't recompute. */
  dateLabel?: string;
  timeLabel?: string;
  format: EventFormat;
  venue?: CommunityVenue;
}

const events: VyEvent[] = [
  {
    slug: "christchurch-sep-26",
    kind: "community",
    title: "Vihangam Yog · An Evening of Meditation & Discourse",
    blurb:
      "An evening introduction to Vihangam Yog in Christchurch — meditation, spiritual discourse, and Mahaprasad together. Free, open to all, no prior experience needed.",
    longCopy:
      "An evening introduction to the Vihangam Yog technique — an ancient meditative practice that withdraws awareness from sensory distraction and settles the mind, bringing clarity, emotional balance, and inner peace. The programme opens at 5:30pm with tea and seating, followed by a spiritual discourse and guided meditation with Sant Shri Naam Deo Ji, and the Amritvani of Sadguru Shri Swatantra Deo Ji Maharaj. We close with Mahaprasad and dinner together. No prior experience needed — simply come as you are.",
    // Sat 26 Sep 2026, 5:30pm NZST (UTC+12; NZ daylight saving begins
    // Sun 27 Sep, so this date is still standard time) = 05:30 UTC.
    startsAt: "2026-09-26T05:30:00.000Z",
    endsAt: "2026-09-26T08:30:00.000Z",
    dateLabel: "Saturday 26 September 2026",
    timeLabel: "5:30 — 8:30 pm NZST",
    format: "in-person",
    venue: {
      name: "64 Kirk Road",
      suburb: "Templeton, Christchurch 8042",
      mapUrl:
        "https://www.google.com/maps/search/?api=1&query=64+Kirk+Road+Templeton+Christchurch+8042",
    },
  },
];

/**
 * Three-state lifecycle for an event:
 *   "upcoming" — hasn't started yet. Registration is open.
 *   "active"   — currently happening. Registration stays open but
 *                reframes as "Check in".
 *   "past"     — endsAt has passed. Form closes; archive notice only.
 */
export type EventPhase = "upcoming" | "active" | "past";

/**
 * The "active" window opens 30 minutes before startsAt so the
 * check-in surface is live for the leading edge of arrivals.
 */
const ACTIVE_LEAD_TIME_MS = 30 * 60 * 1000;

export function getEventPhase(
  event: VyEvent,
  now: Date = new Date(),
): EventPhase {
  if (!event.startsAt) {
    return "upcoming";
  }
  const start = new Date(event.startsAt).getTime();
  const fallbackHours = 4;
  const end = event.endsAt
    ? new Date(event.endsAt).getTime()
    : start + fallbackHours * 60 * 60 * 1000;
  const t = now.getTime();
  if (t < start - ACTIVE_LEAD_TIME_MS) {
    return "upcoming";
  }
  if (t >= end) {
    return "past";
  }
  return "active";
}

/** Convenience around getEventPhase for call sites that only care
 *  about past/not-past. */
export function isEventPast(event: VyEvent, now: Date = new Date()): boolean {
  return getEventPhase(event, now) === "past";
}

export function findEventBySlug(slug: string): VyEvent | undefined {
  return events.find((event) => event.slug === slug);
}

/** Upcoming sessions soonest-first; past sessions most-recent-first. */
export function splitCommunityEvents(now: Date = new Date()) {
  const upcoming: VyEvent[] = [];
  const past: VyEvent[] = [];
  for (const e of events) {
    if (isEventPast(e, now)) {
      past.push(e);
    } else {
      upcoming.push(e);
    }
  }
  upcoming.sort((a, b) => {
    const at = a.startsAt ? new Date(a.startsAt).getTime() : Infinity;
    const bt = b.startsAt ? new Date(b.startsAt).getTime() : Infinity;
    return at - bt;
  });
  past.sort((a, b) => {
    const at = a.startsAt ? new Date(a.startsAt).getTime() : 0;
    const bt = b.startsAt ? new Date(b.startsAt).getTime() : 0;
    return bt - at;
  });
  return { upcoming, past };
}

export function getNextCommunityEvent(now: Date = new Date()): VyEvent | null {
  return splitCommunityEvents(now).upcoming[0] ?? null;
}
